import { ReactElement, useCallback, useMemo } from 'react';
import { Stack, Typography, TextField, Avatar } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import StoreIcon from '@mui/icons-material/StoreMallDirectory';
import { useSnackbar } from 'notistack';
import { useFormik, FormikProvider, FormikHelpers } from 'formik';

import { DataResultsResponse } from '~globals/types/api';
import {
  useAppSelector,
  useAppDispatch,
  useTypePartnerCompany,
  useLazyRequest,
} from '~hooks/index';
import { selectAuth, setUserCompany } from '~store/slices/auth';
import { uploadImages } from '~services/files/index';
import { FileResponse } from '~services/files/types';
import { updateAdminCompany } from '~services/admin/company';
import {
  AdminCompany,
  AdminCompanyUpdate,
} from '~services/admin/company/types';
import { hasError } from '~utils/formHelpers';
import { getTaxIdLabel } from '~utils/billing';
import { checkIsArg } from '~utils/country';
import FileUpload from '~components/FileUpload';

import { DialogConfigAndPreferencesSectionCard } from '../styles';

import { DialogConfigAndPreferencesSectionGeneralProps } from './types';
import {
  FIELDS_NAME,
  getInitialValues,
  validationSchema,
  getOptionalText,
} from './utils';

const DialogConfigAndPreferencesSectionGeneral = ({
  data,
  reload,
}: DialogConfigAndPreferencesSectionGeneralProps): ReactElement => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(selectAuth);
  const { isCarrier } = useTypePartnerCompany();

  const [, loadingUpadteCompany, , executeUpdateCompany] = useLazyRequest({
    request: updateAdminCompany,
    withPostSuccess: (response) => {
      const companyResponse = response.data?.data as AdminCompany;
      dispatch(setUserCompany(companyResponse));

      const message = 'Datos de la compañia actualizados correctamente';
      enqueueSnackbar(message, { variant: 'success' });

      reload();
    },
    withPostFailure: () => {
      const errorMessage =
        'No se ha podido actualizar los datos de la compañia, intente nuevamente';

      enqueueSnackbar(errorMessage, { variant: 'error' });
    },
  });

  const executeSubmit = useCallback(
    async (
      values: ReturnType<typeof getInitialValues>,
      { setSubmitting }: FormikHelpers<ReturnType<typeof getInitialValues>>,
    ) => {
      try {
        const logoValue = values[FIELDS_NAME.LOGO];
        let logoFileId = data?.logoFileId as string;

        if (logoValue.length > 0) {
          const dataUploadFile = await uploadImages(logoValue);
          const {
            data: { results },
          } = dataUploadFile.data as DataResultsResponse<FileResponse[]>;

          logoFileId = results[0].fileId;
        }

        const body: AdminCompanyUpdate = {
          logoFileId,
          name: values[FIELDS_NAME.NAME],
          cuit: values[FIELDS_NAME.CUIT],
          countryId: Number(values[FIELDS_NAME.COUNTRY]),
          timeZone: values[FIELDS_NAME.TIMEZONE],
          address: values[FIELDS_NAME.ADDRESS_STREET],
          number: values[FIELDS_NAME.ADDRESS_NUMBER]
            ? Number(values[FIELDS_NAME.ADDRESS_NUMBER])
            : null,
          floor: values[FIELDS_NAME.ADDRESS_FLOOR],
          department: values[FIELDS_NAME.ADDRESS_DEPARTMENT],
          city: values[FIELDS_NAME.ADDRESS_CITY],
          zipCode: values[FIELDS_NAME.ADDRESS_ZIP_CODE],
          state: values[FIELDS_NAME.ADDRESS_STATE],
          streetBetween1: values[FIELDS_NAME.ADDRESS_STREET_BETWEEN_ONE],
          streetBetween2: values[FIELDS_NAME.ADDRESS_STREET_BETWEEN_TWO],
          contactName: values[FIELDS_NAME.CONTACT_NAME],
          contactIdentityNumber: values[FIELDS_NAME.CONTACT_DOCUMENT],
          contactPhone: values[FIELDS_NAME.CONTACT_PHONE],
          contactMobile: values[FIELDS_NAME.CONTACT_MOBILE],
          contactEmail: values[FIELDS_NAME.CONTACT_EMAIL],
          businessProfileTypeId: user?.company.businessProfileTypeId ?? null,
        };

        await executeUpdateCompany(body);
      } catch (_err) {
        enqueueSnackbar('Ha ocurrido un error, intente nuevamente', {
          variant: 'error',
        });
      } finally {
        setSubmitting(false);
      }
    },
    [data, enqueueSnackbar, executeUpdateCompany, user],
  );

  const formikBag = useFormik({
    initialValues: getInitialValues(data),
    validationSchema: validationSchema(isCarrier),
    onSubmit: executeSubmit,
  });

  const {
    errors,
    touched,
    getFieldProps,
    handleSubmit,
    values,
    isSubmitting,
    dirty,
  } = formikBag;

  const urlLogo = useMemo(() => {
    let output: string | undefined = data?.logoFile?.url;

    const formAvatar = values[FIELDS_NAME.LOGO];

    if (formAvatar.length > 0) output = URL.createObjectURL(formAvatar[0].file);

    return output;
  }, [data, values]);

  return (
    <FormikProvider value={formikBag}>
      <DialogConfigAndPreferencesSectionCard>
        <Stack spacing={2} component="form" onSubmit={handleSubmit}>
          <Stack spacing={1} direction="row" alignItems="center">
            <StoreIcon />

            <Typography variant="h6" component="h5" fontWeight="bold">
              Mi Tienda
            </Typography>
          </Stack>

          <Stack spacing={2}>
            <Stack spacing={2} direction="row" alignItems="center">
              <FileUpload
                name={FIELDS_NAME.LOGO}
                label="Modificar logo"
                enabledChipsInfo={false}
              />

              <Avatar
                src={urlLogo}
                alt={data?.name}
                sx={{ width: 100, height: 100 }}
              />
            </Stack>

            <Typography component="h6" fontWeight="bold" color="text.secondary">
              Información legal
            </Typography>

            <Stack spacing={2} direction="row">
              <TextField
                label="Nombre / Razon social"
                autoComplete="off"
                fullWidth
                error={hasError(touched, errors, FIELDS_NAME.NAME)}
                {...getFieldProps(FIELDS_NAME.NAME)}
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                label={getOptionalText(
                  getTaxIdLabel(checkIsArg(user?.company.countryId)),
                  !isCarrier,
                )}
                autoComplete="off"
                fullWidth
                error={hasError(touched, errors, FIELDS_NAME.CUIT)}
                {...getFieldProps(FIELDS_NAME.CUIT)}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
          </Stack>

          <Stack spacing={2}>
            <Typography component="h6" fontWeight="bold" color="text.secondary">
              Información de contacto
            </Typography>

            <TextField
              label="Email"
              autoComplete="off"
              fullWidth
              error={hasError(touched, errors, FIELDS_NAME.CONTACT_EMAIL)}
              {...getFieldProps(FIELDS_NAME.CONTACT_EMAIL)}
              InputLabelProps={{ shrink: true }}
            />

            <Stack spacing={2} direction="row">
              <TextField
                label="Nombre completo (Opcional)"
                autoComplete="off"
                fullWidth
                error={hasError(touched, errors, FIELDS_NAME.CONTACT_NAME)}
                {...getFieldProps(FIELDS_NAME.CONTACT_NAME)}
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                label="Celular / Whatsapp (Opcional)"
                placeholder="Cod de área sin 0 + Nro sin 15"
                autoComplete="off"
                fullWidth
                error={hasError(touched, errors, FIELDS_NAME.CONTACT_MOBILE)}
                {...getFieldProps(FIELDS_NAME.CONTACT_MOBILE)}
                InputLabelProps={{ shrink: true }}
                helperText="Cod de área sin 0 + Nro sin 15"
              />
            </Stack>
          </Stack>

          <LoadingButton
            type="submit"
            variant="contained"
            color="primary"
            loading={loadingUpadteCompany || isSubmitting}
            disabled={!dirty}
            sx={{ maxWidth: 200 }}
          >
            Guardar
          </LoadingButton>
        </Stack>
      </DialogConfigAndPreferencesSectionCard>
    </FormikProvider>
  );
};

export default DialogConfigAndPreferencesSectionGeneral;
